import PropTypes from "prop-types";
import React from "react";

import { SMALL, useDeviceInfo } from "@swa-ui/browser";
import { HorizontalList, Icon, Image, Link, Text } from "@swa-ui/core";
import { HtmlValue, usePlacement } from "@swa-ui/placements";
import { capitalizeFirstCharacter, classNames } from "@swa-ui/string";

import styles from "./Callouts.module.scss";

/**
 * Displays callout section of global footer based on WCM content.
 */

export const Callouts = (props) => {
  const { className } = props;
  const { screenSize } = useDeviceInfo();
  const { placement: globalNavFooterCalloutV2 } = usePlacement("globalNavFooterCalloutV2");
  const globalNavFooterCallouts = globalNavFooterCalloutV2?.placement?.globalNavFooterCallouts;

  return (
    globalNavFooterCallouts && (
      <div className={classNames(className, styles.callouts)}>
        {screenSize === SMALL ? renderSmallScreen() : renderLargeScreen()}
      </div>
    )
  );

  function renderSmallScreen() {
    return <HorizontalList {...getHorizontalListProps()} />;
  }

  function renderLargeScreen() {
    return (
      <>
        {renderCalloutSections()}
        {renderMobileAppsSection()}
      </>
    );
  }

  function renderCalloutSections() {
    return globalNavFooterCallouts.map((callout, index) => (
      <div key={callout?.calloutColumnHeader?.value}>
        {renderCalloutHeader(callout, index)}
        {renderCalloutLinks(callout, index)}
        {renderSecondaryLinks(callout)}
      </div>
    ));
  }

  function renderCalloutHeader(callout, index) {
    return (
      callout?.calloutColumnHeader?.value && (
        <Text {...getCalloutHeaderProps(callout, index)}>{callout.calloutColumnHeader.value}</Text>
      )
    );
  }

  function renderCalloutLinks(callout, index) {
    return (
      <div className={index === 2 ? styles.socialLinks : undefined}>
        {callout?.calloutColumnPrimaryLinks?.map((link) => (
          <Link {...getCalloutLinkProps(index, link)} key={link?.url}>
            {renderLinkContent(link)}
          </Link>
        ))}
      </div>
    );
  }

  function renderSecondaryLinks(callout) {
    return (
      callout?.calloutColumnSecondaryLinks && (
        <HtmlValue
          className={styles.secondaryLinks}
          htmlValue={callout.calloutColumnSecondaryLinks}
        />
      )
    );
  }

  function renderLinkContent(link) {
    let content = link?.value;

    if (link?.imgPath) {
      content = <Image alt={link?.ariaLabel} src={link.imgPath} />;
    }

    return content;
  }

  function renderMobileAppsSection() {
    const mobileAppsLink = globalNavFooterCallouts?.[2]?.calloutSidecarPrimaryLink?.[0];

    return (
      mobileAppsLink && (
        <div className={styles.mobileApps}>
          <Icon {...getMobilePhoneIconProps(mobileAppsLink)} />
          <Link {...getMobileAppsLinkProps(mobileAppsLink)}>{mobileAppsLink?.value}</Link>
        </div>
      )
    );
  }

  function getHorizontalListProps() {
    return {
      className: styles.horizontalList,
      items: getHorizontalListItems(),
      separator: true,
      spacing: "small",
    };
  }

  function getHorizontalListItems() {
    const footerLinks = [
      {
        link: globalNavFooterCallouts?.[0]?.calloutColumnPrimaryLinks?.[0],
        value: globalNavFooterCallouts?.[0]?.calloutColumnPrimaryLinks?.[0]?.value,
      },
      {
        link: globalNavFooterCallouts?.[1]?.calloutColumnPrimaryLinks?.[0],
        value: globalNavFooterCallouts?.[1]?.calloutColumnHeader?.value,
      },
      {
        link: globalNavFooterCallouts?.[2]?.calloutSidecarPrimaryLink?.[0],
        value: globalNavFooterCallouts?.[2]?.calloutSidecarPrimaryLink?.[0]?.value,
      },
    ];

    return footerLinks.map(({ link, value }) => ({
      componentProps: {
        children: value,
        emphasis: false,
        href: link?.url,
        newWindow: link?.newWindow,
        showUnderline: false,
        size: "fontSize14",
      },
    }));
  }

  function getCalloutHeaderProps(callout, index) {
    return {
      bold: true,
      color: "footer-callout-header-text",
      size: "xsmall",
      suffixIcon:
        index === 2
          ? {
              className: styles.externalIcon,
              name: "External",
            }
          : undefined,
      textType: "display",
    };
  }

  function getCalloutLinkProps(index, link) {
    return {
      "aria-label": link?.ariaLabel,
      emphasis: true,
      href: link?.url,
      newWindow: link?.newWindow,
      showUnderline: false,
      size: index === 1 ? "fontSize20" : "fontSize28",
    };
  }

  function getMobilePhoneIconProps(mobileAppsLink) {
    return {
      className: styles.mobilePhoneIcon,
      color: "footer-callout-phone-icon",
      name: capitalizeFirstCharacter(mobileAppsLink?.imgPath),
      size: "size48",
    };
  }

  function getMobileAppsLinkProps(mobileAppsLink) {
    return {
      href: mobileAppsLink?.url,
      newWindow: mobileAppsLink?.newWindow,
      showUnderline: false,
      size: "fontSize14",
    };
  }
};

Callouts.displayName = "Callouts";
Callouts.propTypes = {
  /**
   * Additional classes for positioning the component. Given classes may only position this component for layout
   * purposes, and cannot change how the component renders in any way.
   */
  className: PropTypes.string,
};
